import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { stringifyError } from '@srm/core/errors';
import { plural } from '@srm/ui/lib/pluralize';
import { BackgroundJobKind } from '@models/backgroundJobs/IBackgroundJob';
import { BackgroundJobAction, backgroundJobKind } from '@services/backgroundJobs/backgroundJobKinds';
import {
    BackgroundJobTask,
    IBackgroundJobResultFile,
    IBackgroundJobWaitOutcome,
    IBackgroundJobWaitProgress,
} from '@services/backgroundJobs/backgroundJobTask';
import { flyToBell } from '@services/backgroundJobs/flyToBell';
import { announceJob, detachedJobAnnouncement } from '@services/backgroundJobs/jobAnnouncer';
import { useFileDownload } from '@hooks/useFileDownload';

/**
 * `idle` — до старта (форма окна); `leaving` — форма уходит; `loading` — идёт работа; `finishing` — итог пришёл, знак
 * дорисовывается; `result` — итог в окне.
 */
export type BackgroundJobRunPhase = 'idle' | 'leaving' | 'loading' | 'finishing' | 'result';

/** Итог в окне. */
export interface IBackgroundJobWaitResult {
    tone: 'ok' | 'err';
    title: string;
    text: string;
    warnings?: string[];
    /** Сколько замечаний всего, если больше показанных в `warnings`. */
    warningsTotal?: number;
    file?: IBackgroundJobResultFile;
    /** Действия сверх файла: открыть документ, страницу результата. */
    actions?: BackgroundJobAction[];
}

export const BACKGROUND_JOB_RUN_TIMINGS = {
    leave: 160,
    /** Короче лоадер не успевает прочитаться — быстрый ответ выглядел бы миганием. */
    minLoading: 600,
    finish: 440,
} as const;

/** Заглушки сервера на успехе: о результате они ничего не говорят. */
const GENERIC_SUCCESS_MESSAGES = new Set(['Обновление данных выполнено.', 'Обработка файла завершена.']);

const meaningful = (message: string) => {
    const text = message.trim();
    return text && !GENERIC_SUCCESS_MESSAGES.has(text) ? text : undefined;
};

export interface IBackgroundJobWaitResultOptions {
    kind?: BackgroundJobKind;
    /** Текст успеха вместо сообщения сервера. */
    successText?: string;
}

/** Итог работы → итог в окне: заголовки и тексты по умолчанию — из реестра видов. */
export const toBackgroundJobWaitResult = (
    outcome: IBackgroundJobWaitOutcome,
    { kind, successText }: IBackgroundJobWaitResultOptions = {},
): IBackgroundJobWaitResult => {
    const texts = backgroundJobKind(kind).wait.outcomes;
    const extra = outcome.actions?.length ? { actions: outcome.actions } : null;
    if (outcome.cancelled) return { tone: 'err', title: texts.cancelled.title, text: texts.cancelled.text, ...extra };
    if (!outcome.success) {
        return {
            tone: 'err',
            title: texts.failed.title,
            text: outcome.message.trim() || texts.failed.text,
            file: outcome.file,
            ...extra,
        };
    }
    const warnings = outcome.warnings?.map((warning) => warning.trim()).filter(Boolean) ?? [];
    const total = Math.max(outcome.warningsTotal ?? 0, warnings.length);
    return {
        tone: 'ok',
        title: texts.succeeded.title,
        text: successText || meaningful(outcome.message) || texts.succeeded.text,
        warnings: total > 0 ? warnings : undefined,
        ...(total > warnings.length ? { warningsTotal: total } : null),
        file: outcome.file,
        ...extra,
    };
};

/** Сколько замечаний у итога в окне. */
export const resultWarningsCount = (result: Pick<IBackgroundJobWaitResult, 'warnings' | 'warningsTotal'>): number =>
    result.warningsTotal ?? result.warnings?.length ?? 0;

/** Фраза с точкой в конце: тексты сервера приходят и с ней, и без неё. */
const sentence = (text: string): string => {
    const trimmed = text.trim();
    return !trimmed || /[.!?…]$/u.test(trimmed) ? trimmed : `${trimmed}.`;
};

/** Текст постоянной live-области окна: ход работы, затем итог; вне работы — пусто. */
export const backgroundJobWaitAnnouncement = (
    phase: BackgroundJobRunPhase,
    loadingText: string,
    result: IBackgroundJobWaitResult | undefined,
): string => {
    if (phase === 'loading') return loadingText;
    if (phase !== 'result' || !result) return '';
    const count = resultWarningsCount(result);
    const warnings = count ? `${count} ${plural(count, ['замечание', 'замечания', 'замечаний'])}.` : '';
    return [sentence(result.title), sentence(result.text), warnings].filter(Boolean).join(' ');
};

export interface IUseBackgroundJobRunOptions {
    /** Вид задачи: тексты окна и итога по умолчанию. */
    kind?: BackgroundJobKind;
    /**
     * Итог — в окне, файл результата — по кнопке в нём (по умолчанию). Без итога в окне успех закрывает окно
     * (`onComplete(true)`), отказ — `errorText`, а файл результата скачивается сразу.
     */
    showResult?: boolean;
    /** Текст успеха вместо сообщения сервера. */
    successText?: string;
    onComplete?: (isSucceed: boolean) => void;
}

const IDLE_PROGRESS: IBackgroundJobWaitProgress = { detachable: false };

type Timers = RefObject<number[]>;

const clearTimers = (timers: Timers) => {
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
};

const later = (timers: Timers, ms: number, action: () => void) => {
    timers.current.push(window.setTimeout(action, ms));
};

type Pending = RefObject<(() => void)[]>;

const dropPending = (pending: Pending) => {
    pending.current.forEach((cancel) => cancel());
    pending.current = [];
};

/**
 * Выполнить, когда вкладку видно: итог в скрытой вкладке ещё никто не видел. Окно закрыли раньше — не выполнять
 * вовсе (`dropPending`).
 */
const whenVisible = (pending: Pending, action: () => void) => {
    if (document.visibilityState !== 'hidden') {
        action();
        return;
    }
    const onVisibility = () => {
        if (document.visibilityState === 'hidden') return;
        cancel();
        action();
    };
    const cancel = () => {
        document.removeEventListener('visibilitychange', onVisibility);
        pending.current = pending.current.filter((item) => item !== cancel);
    };
    document.addEventListener('visibilitychange', onVisibility);
    pending.current.push(cancel);
};

/**
 * Окно, которое ждёт работу на сервере: фазы лоадера и итога, этап и прогресс, «Продолжить в фоне» (`detach`),
 * «увидел» итог — только из видимой вкладки. Работа — любой `BackgroundJobTask` (`startTask`): фоновая задача
 * (`createBackgroundJobTask`) или, например, загрузка одним запросом, когда фон выключен.
 */
export const useBackgroundJobRun = ({
    kind,
    showResult = true,
    successText,
    onComplete,
}: IUseBackgroundJobRunOptions = {}) => {
    const [phase, setPhase] = useState<BackgroundJobRunPhase>('idle');
    const [result, setResult] = useState<IBackgroundJobWaitResult>();
    const [errorText, setErrorText] = useState<string>();
    const [progress, setProgress] = useState<IBackgroundJobWaitProgress>(IDLE_PROGRESS);
    const [settled, setSettled] = useState(false);
    const run = useRef(0);
    const timers = useRef<number[]>([]);
    const pending = useRef<(() => void)[]>([]);
    const watching = useRef<AbortController | null>(null);

    /** Перестать следить за текущей работой: её итог окну больше не нужен. */
    const abandon = useCallback(() => {
        run.current++;
        clearTimers(timers);
        dropPending(pending);
        watching.current?.abort();
        watching.current = null;
    }, []);

    useEffect(() => abandon, [abandon]);

    const startTask = useCallback(
        (task: BackgroundJobTask) => {
            abandon();
            const id = run.current;
            const current = () => run.current === id;
            const controller = new AbortController();
            watching.current = controller;
            const startedAt = Date.now();
            setErrorText(undefined);
            setResult(undefined);
            setProgress(IDLE_PROGRESS);
            setSettled(false);
            if (showResult) {
                setPhase('leaving');
                later(timers, BACKGROUND_JOB_RUN_TIMINGS.leave, () => current() && setPhase('loading'));
            } else {
                setPhase('loading');
            }

            const onProgress = (next: IBackgroundJobWaitProgress) => {
                if (current()) setProgress(next);
            };

            void task({ signal: controller.signal, onProgress })
                .catch((error: unknown): IBackgroundJobWaitOutcome => ({
                    success: false,
                    message: stringifyError(error),
                }))
                .then((outcome) => {
                    if (!current()) return;
                    if (watching.current === controller) watching.current = null;
                    setSettled(true);

                    if (!showResult) {
                        void outcome.file?.download();
                        setPhase('idle');
                        if (outcome.success) onComplete?.(true);
                        else setErrorText(outcome.message.trim() || backgroundJobKind(kind).wait.outcomes.failed.text);
                        return;
                    }

                    const next = toBackgroundJobWaitResult(outcome, { kind, successText });
                    const shownAt =
                        startedAt + BACKGROUND_JOB_RUN_TIMINGS.leave + BACKGROUND_JOB_RUN_TIMINGS.minLoading;
                    later(timers, Math.max(0, shownAt - Date.now()), () => {
                        setResult(next);
                        setPhase('finishing');
                        const onShown = outcome.onShown;
                        if (onShown) whenVisible(pending, onShown);
                        later(timers, BACKGROUND_JOB_RUN_TIMINGS.finish, () => setPhase('result'));
                    });
                });
        },
        [abandon, kind, onComplete, showResult, successText],
    );

    const waitRef = useRef<HTMLDivElement>(null);

    const detach = useCallback(
        (title?: string) => {
            abandon();
            const wait = waitRef.current;
            flyToBell({ from: wait?.closest('[role="dialog"]') ?? wait, kind, title });
            announceJob(detachedJobAnnouncement(kind, title));
        },
        [abandon, kind],
    );

    const reset = useCallback(() => {
        abandon();
        setResult(undefined);
        setErrorText(undefined);
        setProgress(IDLE_PROGRESS);
        setSettled(false);
        setPhase('idle');
    }, [abandon]);

    const { busy: downloadingResultFile, run: runDownload } = useFileDownload();
    const downloadResultFile = useCallback(() => {
        const file = result?.file;
        if (file) void runDownload(() => Promise.resolve(file.download()));
    }, [result, runDownload]);

    return {
        phase,
        result,
        errorText,
        /** Подпись этапа под лоадером; `undefined` — общая. */
        stage: progress.stage,
        /** Этап без цифр хода — для live-области окна. */
        stageName: progress.stageName ?? progress.stage,
        /** Работа идёт на сервере без окна: подсказка, что окно можно закрыть. */
        background: progress.detachable,
        /** Окно можно закрыть посреди работы — итог придёт уведомлением. Пока знак дорисовывается — уже нет. */
        detachable: phase === 'loading' && progress.detachable && !settled,
        busy: phase !== 'idle' && phase !== 'result',
        /** Ждать итог работы. */
        startTask,
        reset,
        /** Тело окна (`BackgroundJobWait`): по нему `detach` находит окно, которое улетит в колокольчик. */
        waitRef,
        /**
         * Окно уходит, работа — в фон: перестать следить за ней, а окно (пока оно ещё на экране — до закрытия)
         * улетает в колокольчик; скринридеру уход в фон объявляет шапка. `title` — название задачи в подсказке у
         * колокольчика и в объявлении.
         */
        detach,
        downloadResultFile,
        /** Файл результата скачивается: кнопка занята, повторный щелчок ничего не делает. */
        downloadingResultFile,
    };
};

export type BackgroundJobRun = ReturnType<typeof useBackgroundJobRun>;
